import { useEffect, useState, useRef, useCallback } from "react";

import { INTERVAL_CHECKING_WALLET } from "constants/network";
import { AutoWalletCheckRequest, RequestCheckWalletDto, RequestCheckWalletStatus } from "interfaces";
import { WalletRiskLevel, RISK_LEVEL_ENUM } from "interfaces";
import { ENDPOINTS, api, errorHandler } from "services";
import { getIdForWalletCheck } from "services";
import { useWalletRiskEvents } from "./useWebSocketNotifications";
import { useStores } from "stores";
import { routes } from "constants/routes";

export const fetchId = async (autoWalletCheckRequest: {
  walletAddress: string;
  requestId?: string;
  quoteId?: string;
  userId?: string;
}): Promise<string | null> => {
  try {
    const response = await getIdForWalletCheck(autoWalletCheckRequest);
    return response.id;
  } catch (error: any) {
    errorHandler(error);
    return null;
  }
};

const checkRiskHandler = async (id: string): Promise<RequestCheckWalletDto | undefined> => {
  try {
    const response = await fetch(ENDPOINTS.walletCheck(id), {
      method: "GET",
      headers: {
        "Content-Type": "application/octet-stream",
        Authorization: `Bearer ${api.getToken()}`,
      },
    });

    if (response.ok) {
      const responseBody = await response.json();

      return responseBody as RequestCheckWalletDto;
    }
  } catch (error: any) {
    console.error(error);
    return undefined;
  }
};

/**
 * Хук для проверки риска кошелька (если нет подключения по ws, то используется опросник)
 *
 * @param address - адрес кошелька для проверки
 * @param requestId - ID заявки (опционально)
 * @param quoteId - ID котировки (опционально)
 * @param userId - ID юзера (опционально)
 * @param checkRisk - флаг для запуска проверки
 */
export const useWalletRisk = (
  { walletAddress, quoteId, requestId, userId }: AutoWalletCheckRequest,
  checkRisk: boolean
) => {
  const { subscribeToWalletRisk, isConnected } = useWalletRiskEvents();
  const { bidCard, bidWalletChecks, user } = useStores();
  const { isCustomer, isProvider, isRiskManager, isLPRiskManager, isUserAdmin, isSystemAdmin } = user.roles;

  const [amlId, setAmlId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [riskResponse, setRiskResponse] = useState<{
    riskLevel: WalletRiskLevel;
    riskStatus: RequestCheckWalletStatus;
  }>({
    riskLevel: undefined,
    riskStatus: undefined,
  });
  const [fallbackToPolling, setFallbackToPolling] = useState(false);
  const timeoutReached = useRef(false);

  const canReceiveWsEvent = isCustomer || isProvider || isRiskManager || isLPRiskManager || isUserAdmin || isSystemAdmin;

  const onSuccessLoadRisk = useCallback(() => {
    const pagePath = window.location.pathname.replace("/", "");

    const isBidInfoPage = pagePath.includes(bidCard.bid.id) && pagePath.includes(routes.bids);

    if (isBidInfoPage) {
      bidCard.updateCardById(bidCard.bid.id);
      const urlParams = new URLSearchParams(window.location.search);
      const isCheckListOpen = urlParams.get("openCheckList") === "open";

      if (isCheckListOpen) {
        bidWalletChecks.fetch();
      }
    }
  }, [bidCard, bidWalletChecks]);

  useEffect(() => {
    const fetchAndSetId = async () => {
      setRiskResponse({
        riskLevel: RISK_LEVEL_ENUM.UNDEFINED,
        riskStatus: undefined,
      });
      setErrorMessage(null);
      timeoutReached.current = false;
      setFallbackToPolling(false);

      const fetchedId = await fetchId({
        walletAddress,
        quoteId,
        requestId,
        userId,
      });
      setAmlId(fetchedId);
    };

    if (checkRisk && walletAddress) {
      fetchAndSetId();
    } else {
      setRiskResponse({
        riskLevel: undefined,
        riskStatus: undefined,
      });
      setAmlId(null);
      setErrorMessage(null);
      timeoutReached.current = false;
      setFallbackToPolling(false);
    }
  }, [checkRisk, quoteId, requestId, userId, walletAddress]);

  useEffect(() => {
    if (!checkRisk || !amlId) {
      return;
    }

    if (isConnected && canReceiveWsEvent) {
      const unsubscribe = subscribeToWalletRisk(amlId, (event) => {
        setRiskResponse({
          riskLevel: event.riskLevel,
          riskStatus: event.riskStatus,
        });
        onSuccessLoadRisk();
      });

      return unsubscribe;
    } else {
      setFallbackToPolling(true);
    }
  }, [amlId, canReceiveWsEvent, checkRisk, isConnected, onSuccessLoadRisk, subscribeToWalletRisk]);

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;

    const fetchStatus = async () => {
      if (amlId && !timeoutReached.current) {
        const response = await checkRiskHandler(amlId);

        if (response?.riskLevel || response?.riskStatus) {
          setRiskResponse({
            riskLevel: response.riskLevel,
            riskStatus: response.riskStatus,
          });
          onSuccessLoadRisk();
          if (intervalId) clearInterval(intervalId);
        }
      }
    };

    if (checkRisk && amlId && fallbackToPolling && !timeoutReached.current) {
      // fetchStatus();
      intervalId = setInterval(fetchStatus, INTERVAL_CHECKING_WALLET);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [checkRisk, fallbackToPolling, amlId, onSuccessLoadRisk]);

  return {
    ...riskResponse,
    amlId,
    errorMessage,
  };
};
